import React, { useState } from 'react';
import { Trash2, Film, Search, Download, Eye, AlertTriangle } from 'lucide-react';
import { VideoListItem, adminVideoService } from '@/services/adminVideoService';

interface VideoManagementTableProps {
  videos: VideoListItem[];
  isLoading: boolean;
  onDelete: (courseId: string, topicId: string) => Promise<void>;
  onRefresh: () => void;
}

export default function VideoManagementTable({
  videos,
  isLoading,
  onDelete,
  onRefresh,
}: VideoManagementTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'upload_date' | 'size_mb' | 'course_title'>('upload_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedVideo, setSelectedVideo] = useState<VideoListItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<VideoListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter and sort videos
  const filteredVideos = videos.filter(video => {
    const search = searchTerm.toLowerCase();
    return (
      video.course_title.toLowerCase().includes(search) ||
      video.segment_id.toLowerCase().includes(search) ||
      (video.segment_title && video.segment_title.toLowerCase().includes(search))
    );
  });

  const sortedVideos = [...filteredVideos].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'upload_date':
        comparison = new Date(a.upload_date).getTime() - new Date(b.upload_date).getTime();
        break;
      case 'size_mb':
        comparison = a.size_mb - b.size_mb;
        break;
      case 'course_title':
        comparison = a.course_title.localeCompare(b.course_title);
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleSort = (field: typeof sortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (video: VideoListItem) => {
    setIsDeleting(true);
    try {
      await onDelete(video.course_id, video.topic_id || video.segment_id);
      setShowDeleteConfirm(null);
      onRefresh();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (sizeMb: number) => {
    if (sizeMb < 1024) {
      return `${sizeMb.toFixed(2)} MB`;
    }
    return `${(sizeMb / 1024).toFixed(2)} GB`;
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by course name, segment ID, or title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading videos...</p>
          </div>
        ) : sortedVideos.length === 0 ? (
          <div className="p-12 text-center">
            <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              {searchTerm ? 'No videos found' : 'No videos uploaded yet'}
            </h3>
            <p className="text-gray-500 text-sm">
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Click "Upload Video" to add your first video'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/40 border-b border-white/10">
                <tr>
                  <th className="text-left px-6 py-4">
                    <button
                      onClick={() => handleSort('course_title')}
                      className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                      Course
                      {sortField === 'course_title' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-300">
                    Segment
                  </th>
                  <th className="text-left px-6 py-4">
                    <button
                      onClick={() => handleSort('size_mb')}
                      className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                      Size
                      {sortField === 'size_mb' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-left px-6 py-4">
                    <button
                      onClick={() => handleSort('upload_date')}
                      className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                      Upload Date
                      {sortField === 'upload_date' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {sortedVideos.map((video, index) => (
                  <tr
                    key={`${video.course_id}-${video.segment_id}-${index}`}
                    className="hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">
                          {video.course_title}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          ID: {video.course_id}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white">
                          {video.segment_id}
                        </p>
                        {video.segment_title && (
                          <p className="text-gray-500 text-xs mt-1">
                            {video.segment_title}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {formatFileSize(video.size_mb)}
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {formatDate(video.upload_date)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedVideo(video)}
                          className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                          title="View details"
                        >
                          <Eye className="w-4 h-4 text-gray-400 group-hover:text-white" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(video)}
                          className="p-2 bg-white/5 hover:bg-red-900/30 rounded-lg transition-colors group"
                          title="Delete video"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Video Details Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-purple-500/30 max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">Video Details</h3>
            <div className="space-y-3">
              <div>
                <p className="text-gray-400 text-sm">Course</p>
                <p className="text-white">{selectedVideo.course_title}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Segment</p>
                <p className="text-white">
                  {selectedVideo.segment_id}
                  {selectedVideo.segment_title && (
                    <span className="text-gray-400 ml-2">
                      - {selectedVideo.segment_title}
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">File Size</p>
                <p className="text-white">{formatFileSize(selectedVideo.size_mb)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Upload Date</p>
                <p className="text-white">{formatDate(selectedVideo.upload_date)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">S3 Key</p>
                <p className="text-white text-xs font-mono bg-black/40 p-2 rounded">
                  {selectedVideo.s3_key}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedVideo(null)}
              className="mt-6 w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-red-500/30 max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-xl font-bold">Delete Video</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Are you sure you want to delete this video? This action cannot be undone.
            </p>
            <div className="bg-black/40 rounded-lg p-3 mb-6">
              <p className="text-white font-medium">
                {showDeleteConfirm.course_title}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Segment: {showDeleteConfirm.segment_id}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete Video'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}